import type {ErrorLogRef, Instant, UUID} from './common'
import type {ReportCategoryType} from './report-category-type'

export interface BulkDeleteRequest {
    queryVersions: Record<string, number>
}

export interface BulkMoveRequest {
    queryVersions: Record<string, number>
    targetCollectionId: UUID
}

export interface MoveCategoryRequest {
    parentCategoryId?: UUID
    version: number
}

export interface MoveReportRequest {
    targetCategoryId: UUID
    version: number
}

export interface ReorderItem {
    id: UUID
    displayOrder: number
}

export interface ReorderRequest {
    items: ReorderItem[]
}

export interface ReportCategoryCreateRequest {
    name: string
    description?: string
    type: ReportCategoryType
    parentCategoryId?: UUID
}

export interface ReportCategoryUpdateRequest {
    name: string
    description?: string
    version: number
}

export interface ReportDefinitionCreateRequest {
    name: string
    description?: string
    queryDefinition: Record<string, unknown>
}

export interface SqlAssistRequest {
    request: string
    currentSql?: string
    errorMessage?: string
    model?: string
    mode?: string
}

export interface SqlExecuteRequest {
    sql: string
    maxRows: number
    dryRun?: boolean
}

export interface TransferCategoryRequest {
    newType: ReportCategoryType
    version: number
}

export interface UpdateQueryRequest {
    name: string
    description?: string
    version: number
    queryDefinition?: Record<string, unknown>
    scheduleCron?: string | null
    recipientIdentityIds?: UUID[]
    recipientEmails?: string[]
    notificationTemplateKey?: string | null
}

export interface ReportExecution {
    id: UUID
    reportDefinitionId: UUID
    executedBy: UUID | null
    status: string
    parameterValues: Record<string, unknown>
    resultData: Record<string, unknown>[] | null
    rowCount: number | null
    errorMessage: string | null
    startedAt: Instant
    completedAt: Instant | null
    durationMs: number | null
    errorLogRef: ErrorLogRef | null
}

/* eslint-disable i18next/no-literal-string -- field name constants for backend API */
export const BulkDeleteRequestFields = {
    queryVersions: 'queryVersions',
} as const

export const BulkMoveRequestFields = {
    queryVersions: 'queryVersions',
    targetCollectionId: 'targetCollectionId',
} as const

export const MoveCategoryRequestFields = {
    parentCategoryId: 'parentCategoryId',
    version: 'version',
} as const

export const MoveReportRequestFields = {
    targetCategoryId: 'targetCategoryId',
    version: 'version',
} as const

export const ReorderRequestFields = {
    items: 'items',
} as const

export const ReportCategoryCreateRequestFields = {
    name: 'name',
    description: 'description',
    type: 'type',
    parentCategoryId: 'parentCategoryId',
} as const

export const ReportCategoryUpdateRequestFields = {
    name: 'name',
    description: 'description',
    version: 'version',
} as const

export const ReportDefinitionCreateRequestFields = {
    name: 'name',
    description: 'description',
    queryDefinition: 'queryDefinition',
} as const

export const ReportDefinitionListItemFields = {
    reportDefinitionId: 'reportDefinitionId',
    name: 'name',
    description: 'description',
    categoryName: 'categoryName',
    categoryId: 'categoryId',
    updatedAt: 'updatedAt',
    lastRunAt: 'lastRunAt',
    version: 'version',
} as const

export const ReportDefinitionDetailFields = {
    ...ReportDefinitionListItemFields,
    queryDefinition: 'queryDefinition',
    parameters: 'parameters',
    retention: 'retention',
    ownerAuthUserId: 'ownerAuthUserId',
    createdAt: 'createdAt',
    scheduleCron: 'scheduleCron',
    recipientIdentityIds: 'recipientIdentityIds',
    recipientEmails: 'recipientEmails',
    notificationTemplateKey: 'notificationTemplateKey',
} as const

export const ReportExecutionResponseFields = {
    id: 'id',
    reportDefinitionId: 'reportDefinitionId',
    executedBy: 'executedBy',
    status: 'status',
    parameterValues: 'parameterValues',
    resultData: 'resultData',
    rowCount: 'rowCount',
    errorMessage: 'errorMessage',
    startedAt: 'startedAt',
    completedAt: 'completedAt',
    durationMs: 'durationMs',
    errorLogRef: 'errorLogRef',
} as const

export const SqlAssistRequestFields = {
    request: 'request',
    currentSql: 'currentSql',
    errorMessage: 'errorMessage',
    model: 'model',
    mode: 'mode',
} as const

export const SqlAssistResponseFields = {
    suggestion: 'suggestion',
} as const

export const SqlExecuteRequestFields = {
    sql: 'sql',
    maxRows: 'maxRows',
    dryRun: 'dryRun',
} as const

export const SqlExecuteResponseFields = {
    columns: 'columns',
    rows: 'rows',
    rowCount: 'rowCount',
    truncated: 'truncated',
} as const

export const TransferCategoryRequestFields = {
    newType: 'newType',
    version: 'version',
} as const

export const UpdateQueryRequestFields = {
    name: 'name',
    description: 'description',
    version: 'version',
    queryDefinition: 'queryDefinition',
    scheduleCron: 'scheduleCron',
    recipientIdentityIds: 'recipientIdentityIds',
    recipientEmails: 'recipientEmails',
    notificationTemplateKey: 'notificationTemplateKey',
} as const
/* eslint-enable i18next/no-literal-string */
